// src/utils/faviconBadge.ts
const cache = new Map<string, string>();

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

function drawCircularBase(ctx: CanvasRenderingContext2D, img: HTMLImageElement, size: number) {
  // Clear to transparent
  ctx.clearRect(0, 0, size, size);

  // Clip circle
  ctx.save();
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  // Draw image to fill
  ctx.drawImage(img, 0, 0, size, size);
  ctx.restore();
}

export async function createCircularFaviconDataUrl(baseHref: string): Promise<string | null> {
  const key = `circle__${baseHref}`;
  if (cache.has(key)) return cache.get(key)!;

  try {
    const img = await loadImage(baseHref);

    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    drawCircularBase(ctx, img, size);

    const dataUrl = canvas.toDataURL('image/png');
    cache.set(key, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}

export async function createCircularBadgedFaviconDataUrl(
  baseHref: string,
  count: number = 1
): Promise<string | null> {
  const key = `circle_badge__${baseHref}__${count}`;
  if (cache.has(key)) return cache.get(key)!;

  try {
    const img = await loadImage(baseHref);

    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Circular base
    drawCircularBase(ctx, img, size);

    // Badge
    const r = Math.floor(size * 0.22);
    const cx = size - r - 4;
    const cy = r + 4;

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();

    const text = count > 99 ? '99+' : String(count);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.floor(r * 1.2)}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cx, cy + 1);

    const dataUrl = canvas.toDataURL('image/png');
    cache.set(key, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}